import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../api/user/auth';
import { logoutHotel } from '../api/hotel/hotelApi';
import { HOTEL_BASE, hotelPaths } from '../features/hotel/paths';

// Set by the API on user login (not httpOnly) so the UI knows a user session exists
const hasUserSession = () => document.cookie.split('; ').includes('is_logged_in=true');

const useAutoLogout = (timeoutMinutes = 60) => {
  const navigate = useNavigate();

  const clearAllTokens = useCallback(() => {
    console.log('All tokens cleared due to inactivity');
  }, []);

  // Hotels have their own account type and login page; everyone else is a user.
  // Visitors who are not signed in are left alone.
  const logout = useCallback(async () => {
    try {
      if (window.location.pathname.startsWith(HOTEL_BASE)) {
        await logoutHotel();
        navigate(hotelPaths.login, { replace: true });
      } else if (hasUserSession()) {
        await logoutUser();
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [navigate]);

  useEffect(() => {
    let inactivityTimer;

    const resetTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }

      const timeoutMs = timeoutMinutes * 60 * 1000;

      inactivityTimer = setTimeout(() => {
        console.log('Auto logout due to inactivity');
        logout();
      }, timeoutMs);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'load'];

    const resetTimerOnActivity = () => {
      resetTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, resetTimerOnActivity);
    });

    resetTimer();

    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimerOnActivity);
      });
    };
  }, [timeoutMinutes, logout]);

  return { logout, clearAllTokens };
};

export default useAutoLogout;
